import 'intersection-observer'
import React, { ChangeEventHandler, useCallback, useMemo, useState, useEffect } from 'react'
import styled from 'styled-components'
import tw from 'tailwind-styled-components'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import debounce from 'lodash/debounce'
import { useTranslation } from 'next-i18next'
import { ListAuctionsQueryVariables, usePaginatedListAuctionsQuery } from '@foros-fe/graphql'
import { clientOnlyComponent, clientOnlyModule, SortOption } from '../../utils'
import { AuctionListContextProvider } from '../../contexts/AuctionList'
import { AuctionCard } from '../AuctionCard'
import { TextInput } from '../Inputs/TextInput'
import { CheckBox } from '../Inputs/CheckBox'
import { Select, Props as SelectProps } from '../Inputs/Select'
import { Spinner } from '../Spinner'
import { auctionsFilterKey, domains } from '../../config'
import { PopupRangeFilter } from './PopupRangeFilter'
import { PopupMultiSelectFilter } from './PopupMultiSelectFilter'
import { filterStateToQueryVariables, defaultFilterProps, RangeValue } from './utils'
import AreaIcon from '@foros-fe/assets/icons/area-icon.png'
import PriceIcon from '@foros-fe/assets/icons/price.png'

const AuctionListMap = clientOnlyModule(
  () => import('@foros-fe/core/components/Maps/AuctionListMap')
)

const Flex1 = tw.div`
  flex
  flex-1
`
const Container = tw(Flex1)`
  w-screen
  flex-col-reverse
  lg:flex-row
`
const ListContainer = tw.div`flex flex-col w-full lg:w-2/3 p-6 md:pt-16`
const TitleContainer = tw.div`
  flex
  flex-col
  lg:flex-row
  items-start
  lg:items-center
  justify-between
  flex-wrap
  mb-5
`
const List = tw(Flex1)`flex-col space-y-8 mb-4`
const CardsContainer = tw(Flex1)`
  flex-col
  space-y-5
`
const MapContainer = tw.div`
  h-[420px]
  lg:h-screen
  w-full
  lg:w-1/3
  lg:sticky
  top-0
  right-0
  border-l
  z-50
  border-gray-400
`
const MessageContainer = tw(Flex1)`flex-col items-center justify-center`
const LineContainer = tw.div`flex flex-wrap`
const StyledTextInput = styled(TextInput)`
  max-width: 370px;
  min-width: 195px;
  margin-right: 10px;
`

export const AuctionList = clientOnlyComponent(() => {
  const { t } = useTranslation(['auctions', 'enum'])
  const options = useMemo(
    () => [
      { value: 'relevance/desc', label: t('sorts.relevanceDescending') },
      { value: 'currentBidPrice/asc', label: t('sorts.priceAscending') },
      { value: 'currentBidPrice/desc', label: t('sorts.priceDescending') },
      { value: 'endTime/desc', label: t('sorts.endTimeDescending') },
      { value: 'endTime/asc', label: t('sorts.endTimeAscending') },
      { value: 'startTime/desc', label: t('sorts.startTimeDescending') },
      { value: 'timesViewed/desc', label: t('sorts.viewsDescending') },
    ],
    [t]
  )
  const filterProps = useMemo(
    () => ({
      ...defaultFilterProps,
      forestType: {
        options: defaultFilterProps.forestType.options.map(({ label, value }) => ({
          label: t(`enum:ForestType.${label}`),
          value,
        })),
      },
      salePurpose: {
        options: defaultFilterProps.salePurpose.options.map(({ label, value }) => ({
          label: t(`enum:AuctionSalePurpose.${label}`),
          value,
        })),
      },
      maturity: {
        options: defaultFilterProps.maturity.options.map(({ label, value }) => ({
          label: t(`enum:TaxonomicPlotMaturityCondition.${label}`),
          value,
        })),
      },
      country: {
        options: defaultFilterProps.country.options.map(({ label, value }) => ({
          label: t(`enum:CountryCode.${label}`),
          value,
        })),
      },
    }),
    [t]
  )
  const initialSortOption = options[4]
  const initialQueryVariables = useMemo(() => {
    const { name, countryCode } = domains.current

    return {
      auctionOpen: true,
      ...filterStateToQueryVariables({
        sortOrder: initialSortOption,
        country: name === 'foros.lv' ? [countryCode] : undefined,
      }),
    }
  }, [initialSortOption])

  const savedQueryVariables = JSON.parse(sessionStorage.getItem(auctionsFilterKey) || '{}')
  const [queryVariables, setQueryVariables] = useState<ListAuctionsQueryVariables>({
    ...initialQueryVariables,
    ...savedQueryVariables,
  })

  const { data, loading, fetchMore, hasNextPage } = usePaginatedListAuctionsQuery(queryVariables)
  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore: () => fetchMore(queryVariables),
  })

  const handleFilterChange = useCallback(
    (changedQueryVariables: ListAuctionsQueryVariables) =>
      setQueryVariables({ ...queryVariables, ...changedQueryVariables }),
    [queryVariables]
  )
  const createFilterHandler = useCallback(
    (name: string) => (value: RangeValue | string[]) =>
      handleFilterChange(filterStateToQueryVariables({ [name]: value })),
    [handleFilterChange]
  )
  const handleInputChange = debounce<ChangeEventHandler<HTMLInputElement>>(
    ({ target }) => handleFilterChange({ [target.name]: target.value }),
    500
  )
  const handleCheckboxChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    ({ target }) => {
      const { checked } = target

      handleFilterChange({
        ...filterStateToQueryVariables({ sortOrder: checked ? initialSortOption : options[0] }),
        [target.name]: checked ? true : null,
      })
    },
    [handleFilterChange, options, initialSortOption]
  )
  const handleSelectChange = useCallback<NonNullable<SelectProps<SortOption, false>['onChange']>>(
    (option, { name }) => handleFilterChange(filterStateToQueryVariables({ [name!]: option! })),
    [handleFilterChange]
  )

  useEffect(() => {
    sessionStorage.setItem(auctionsFilterKey, JSON.stringify(queryVariables))
  }, [queryVariables])

  const auctions = useMemo(() => data?.auctions.edges.map(({ node }) => node!), [data])
  const sortOption = useMemo(() => {
    const { order } = queryVariables

    if (order) {
      const optionValue = Object.entries(order)[0].join('/')

      return options.find(({ value }) => value === optionValue)
    }

    return initialSortOption
  }, [options, initialSortOption, queryVariables])

  return (
    <AuctionListContextProvider>
      <Container>
        <ListContainer>
          <TitleContainer>
            <p className='mb-4 lg:mb-0 font-poppins font-bold text-4xl'>{t('title')}</p>
            <CheckBox
              name='auctionOpen'
              label={t('showOnlyOpenAuctions')}
              onChange={handleCheckboxChange}
              defaultChecked={!!queryVariables.auctionOpen}
            />
          </TitleContainer>
          <List>
            <LineContainer className='-mb-2 -mr-4'>
              <PopupRangeFilter
                title={t('price')}
                onChange={createFilterHandler('price')}
                icon={PriceIcon}
                {...filterProps.price}
                min={queryVariables['priceCentsGteq'] as number}
                max={queryVariables['priceCentsLteq'] as number}
              />
              <PopupRangeFilter
                title={t('area')}
                onChange={createFilterHandler('area')}
                icon={AreaIcon}
                {...filterProps.area}
                min={queryVariables['areaGteq'] as number}
                max={queryVariables['areaLteq'] as number}
              />
              <PopupRangeFilter
                title={t('volume')}
                onChange={createFilterHandler('volume')}
                {...filterProps.volume}
                min={queryVariables['volumeGteq'] as number}
                max={queryVariables['volumeLteq'] as number}
              />
              <PopupMultiSelectFilter
                title={t('forestType')}
                onChange={createFilterHandler('forestType')}
                defaultValue={queryVariables['forestTypeIn'] as string[]}
                {...filterProps.forestType}
              />
              <PopupMultiSelectFilter
                title={t('salePurpose')}
                onChange={createFilterHandler('salePurpose')}
                defaultValue={queryVariables['salePurposeIn'] as string[]}
                {...filterProps.salePurpose}
              />
              <PopupMultiSelectFilter
                title={t('maturity')}
                onChange={createFilterHandler('maturity')}
                defaultValue={queryVariables['maturityIn'] as string[]}
                {...filterProps.maturity}
              />
              <PopupMultiSelectFilter
                title={t('country')}
                onChange={createFilterHandler('country')}
                defaultValue={queryVariables['countryCodeIn'] as string[]}
                {...filterProps.country}
              />
            </LineContainer>
            <LineContainer className='items-end'>
              <div className='flex flex-1 items-end'>
                <StyledTextInput
                  name='titleCont'
                  label={t('keywordSearch')}
                  onChange={handleInputChange}
                  defaultValue={queryVariables['titleCont'] as string}
                />
              </div>
              <Select
                name='sortOrder'
                label={t('sortOrder')}
                options={options}
                className='lg:ml-40'
                onChange={handleSelectChange}
                value={sortOption}
              />
            </LineContainer>
            <CardsContainer>
              {loading ? <Spinner /> : null}
              {auctions?.length ? (
                <>
                  {auctions.map((auction) => (
                    <AuctionCard key={auction.id} {...auction} />
                  ))}
                  {hasNextPage ? <Spinner ref={sentryRef} /> : null}
                </>
              ) : loading ? null : (
                <MessageContainer>
                  <p className='font-semibold text-xl'>{t('common:noResultsFound')}</p>
                  <p className='text-gray-700'>{t('tryDifferentCriteria')}</p>
                </MessageContainer>
              )}
            </CardsContainer>
          </List>
        </ListContainer>
        <MapContainer>
          <AuctionListMap auctions={data?.filteredAuctions.nodes} disableFallback />
        </MapContainer>
      </Container>
    </AuctionListContextProvider>
  )
})
